import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { Button } from 'reactstrap';

import { useNavigate } from 'react-router-dom';

import {
    Tooltip
} from '@mui/material';

import {
    EntityAccessResponse,
    EntityDetailResponse,
    PortalTextCommon,
    PortalTextPaginationControls,
    PortalTextWorkspaceListPage,
    PortalTextWorkspaceManagement,
    UserResponse
} from '../../../Models/Api/strongbox.financialportal';

import { GetNewWorkspacesDisabled, GetShareableLinkParamsFlowSwitch, GetStrongboxUrl } from '../../../Store/AppSettings';
import {
    CustomerFinancialsTarget,
    GetImportCustomerFinancialsTarget,
    actionCreators as ImportFinancialsActions
} from '../../../Store/ImportFinancials';
import { actionCreators as NewDataCollectionParametersActions } from '../../../Store/NewDataCollectionParameters';
import {
    GetPortalText,
} from '../../../Store/PortalAssets';
import { GetRunningJobs, GetRunningJobsVersion, RunningImport, actionCreators as RunningImportsActions } from '../../../Store/RunningImports';
import { actionCreators as SubmissionHistoryActions } from '../../../Store/SubmissionHistory';
import {
    GetModifyWorkspaceUsersState,
    GetPortalBusyState,
    ModifyWorkspaceUsersState,
    actionCreators as UIStateActions,
} from '../../../Store/UIState';
import {
    GetLoggedInUser,
    GetUsers,
    LoadAllUsers,
    LoggedInUserHasAccess
} from '../../../Store/User';

import { LogException, LogMessage, SeverityLevel } from '../../../Utils/Logging';
import { StringFormat } from '../../../Utils/StringUtils';
import { FullUserName } from '../../../Utils/UserUtils';

import { RefreshButton } from '../../Utils/RefreshButton';

import {
    GetWorkspaceInfo,
    LoadWorkspaceDetails,
    LoadWorkspaces,
    LoadingWorkspaces,
    WorkspaceDelete,
    WorkspacePageInfo,
    WorkspaceWithMeta,
    actionCreators as WorkspacesActions
} from '../../../Store/Workspaces';

import { PaginationControls } from '../../PaginationControls/PaginationControls';

import { InlineStylingValues, pathConstants } from '../../../Utils/Constants';
import { InputStringContainsDangerousContent } from '../../../Utils/FormUtils';
import { GetStdThemeColors } from '../../../Utils/Style';
import { BuildQueryString } from '../../../Utils/WorkspaceRouteInformation';

import { NewestSubmissionActions } from '../../NewestSubmissionActions/NewestSubmissionActions';

import { RequestWorkspaceAccess, UpdateWorkspaceUsers, UpsertWorkspaceEntity, WorkspaceActionEnabled } from '../../../Services/WorkspaceService';
import { AddModifyWorkspace, AddModifyWorkspaceErrorState } from '../../AddModifyWorkspace/AddModifyWorkspace';

import { RunningImportsList } from '../../RunningImportsList/RunningImportsList';

import { EventCategory, MetricsService } from '../../../Utils/Metrics';

import { ConfirmModal, ConfirmModalType } from '../../ConfirmModal/ConfirmModal';
import { ErrorBanner, ErrorState } from '../../ErrorBanner/ErrorBanner';

import {
    BrandConfig,
    GetBasicSettings,
    GetBrandConfig,
    TenantBasicSettings,
} from '../../../Store/Tenant';

import { ApplicationState } from '../../../Store';

import { NewDataCollectionButton } from '../../NewDataCollectionButton/NewDataCollectionButton';

import {
    faAdd,
    faLock,
    faTrash,
    faUserGroup,
} from '@awesome.me/kit-8a0d2fd588/icons/modules/sharp/light';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type InjectedReduxState = {
    allUsers: UserResponse[];
    strongboxUrl: string;
    customerFinancialsTarget: CustomerFinancialsTarget | undefined;
    jobs: RunningImport[];
    importsListKey: string;
    workspaceListKey: string;
    linkParamsActive: boolean;
    userCanAddWorkspace: boolean;
    basicTenantSettings?: TenantBasicSettings;
    workspacesAreLoading: boolean;
    workspaces: WorkspaceWithMeta[];
    workspacePageInfo: WorkspacePageInfo | undefined;
    loggedInUser?: UserResponse;
    portalBusy: boolean;
    brandConfig: BrandConfig;
    disableNewWorkspaces: boolean;
    modifyWorkspaceUsers?: ModifyWorkspaceUsersState;
    workspaceListText?: PortalTextWorkspaceListPage;
    paginationControlsText?: PortalTextPaginationControls;
    commonText?: PortalTextCommon;
    workspaceManagementText?: PortalTextWorkspaceManagement;
    textIsAllLoaded: boolean;
    thereAreWorkspaces: boolean;
};

type InjectedActionCreators =
    typeof ImportFinancialsActions &
    typeof NewDataCollectionParametersActions &
    typeof RunningImportsActions &
    typeof WorkspacesActions &
    typeof SubmissionHistoryActions &
    typeof UIStateActions;

type WorkspaceListProps = {
    onWorkspaceUsersChanged?: () => void;
};

type Props = WorkspaceListProps & InjectedReduxState & InjectedActionCreators;

type ConfirmingWorkspaceOp = {
    confirmingWorkspaceId: string;  // either the id of the workspace or undefined meaning the window shouldn't be shown.
    confirmingWorkspaceName: string;
};

export type AddModifyWorkspaceData = {
    displayName?: string;
    engagementCode?: string;
}

type AddWorkspaceState = {
    addWorkspaceModalOpen: boolean;
    showNameRequired: boolean;
    showUnsecureWorkspaceName: boolean;
    showUnsecureEngagementCode: boolean;
    addModifyWorkspaceData: AddModifyWorkspaceData;
};

const WorkspaceListComponent: React.FC<Props> = (props): React.ReactElement => {
    const searchFieldId = 'searchTermInput';

    const [addWorkspace, setAddWorkspace] = React.useState<AddWorkspaceState>({
        addModifyWorkspaceData: {
            displayName: undefined,
            engagementCode: undefined
        },
        showNameRequired: false,
        showUnsecureWorkspaceName: false,
        showUnsecureEngagementCode: false,
        addWorkspaceModalOpen: false,
    });

    const [errorState, setErrorState] = React.useState<ErrorState | undefined>(undefined);
    const [searchWord, setSearchWord] = React.useState<string>('');
    const [confirmingWorkspace, setConfirmingWorkspace] = React.useState<ConfirmingWorkspaceOp | undefined>(undefined);
    const [failedToLoadUsers, setFailedToLoadUsers] = React.useState<boolean>(false);
    const [queryConfirmAddToWorkspace, setQueryConfirmAddToWorkspace] = React.useState<WorkspaceWithMeta | undefined>(undefined);
    const [accessRequested, setAccessRequested] =
        React.useState<
            {
                workspace: WorkspaceWithMeta;
                response: EntityAccessResponse;
            } | undefined>(undefined);

    const [initialized, setInitialized] = React.useState<boolean>(false);

    // Will be undefined or a workspace id
    const [workspaceActionMsgLocked, setWorkspaceActionMsgLocked] = React.useState<string | undefined>(undefined);

    const navigate = useNavigate();

    const loadWorkspacesForCurrentSettings = (hard?: boolean): void => {
        if ((!props.workspacePageInfo) || (props.workspacePageInfo.currentPage === -1)) {
            props.SetPortalWorking('WorkspaceList:loadWorkspacesForCurrentSettings:unscoped');
            LoadWorkspaces(1, '')
                .finally(() => {
                    props.SetPortalIdle('WorkspaceList:loadWorkspacesForCurrentSettings:unscoped');
                });
        } else {
            // Just use what we already have loaded, assuming there's something actually loaded
            if (searchWord !== props.workspacePageInfo.searchTerm) {
                setSearchWord(props.workspacePageInfo.searchTerm);
            }
            if (!props.workspaces || props.workspaces.length <= 0 || hard === true) {
                props.SetPortalWorking('WorkspaceList:loadWorkspacesForCurrentSettings:scoped');
                LoadWorkspaces(props.workspacePageInfo.currentPage, props.workspacePageInfo.searchTerm)
                    .finally(() => {
                        props.SetPortalIdle('WorkspaceList:loadWorkspacesForCurrentSettings:scoped');
                    });
            }
        }
    }

    React.useEffect(() => {
        // The parameter to loadWorkspacesForCurrentSettings was added as a performance optimization when things
        // were really slow.  Better performance now so generally it's not needed (or more accurately passing true for
        // a hard refresh is okay) but I'd prefer not to remove that behavior altogether so it's easy to restore.
        loadWorkspacesForCurrentSettings(true);

        // We need a list of users to add a workspace so the user may select who has access to the new workspace
        // Users aren't likely to change very much, this call to LoadAllUsers is required if, e.g. the user 
        // refreshes the page and the redux store was cleared.  
        props.SetPortalWorking('WorkspaceList:useEffect');
        LoadAllUsers((msg) => {
            setErrorState({
                summaryMessage: msg,
                extraInformation: props.workspaceListText?.errorCannotAddNewWorkspaces || '',
            });
            setFailedToLoadUsers(true);
        })
            .finally(() => {
                props.SetPortalIdle('WorkspaceList:useEffect');
            })

        setInitialized(true);

        // I want this to execute equivalent to componentDidMount so this is appropriate
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addModifyErrorState: AddModifyWorkspaceErrorState | undefined = React.useMemo(() => {
        if (addWorkspace.showNameRequired) {
            return 'nameRequired';
        } else if (addWorkspace.showUnsecureWorkspaceName) {
            return 'unsecureWorkspaceName';
        } else if (addWorkspace.showUnsecureEngagementCode) {
            return 'unsecureEngagementCode';
        }
        return undefined;
    }, [addWorkspace]);

    React.useEffect(() => {
        if (!initialized) {
            return;
        }
        const debounceTimer = setTimeout(() => {
            LoadWorkspaces(1, searchWord)
                .then(() => {
                    const searchField = document.getElementById(searchFieldId);
                    if (!!searchField) {
                        searchField.focus();
                    }
                });
        }, 1000);
        return () => {
            clearTimeout(debounceTimer);
        }

        // lint will complain about initialized not in dependency list, but based on how I use that, I do not want that.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchWord]);

    const searchOnChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
        if (e.target.value !== '') {
            setSearchWord(e.target.value);
        } else {
            setSearchWord('');
        }
    }

    const pageLeft = (): void => {
        if ((!props.workspacePageInfo) || (props.workspacePageInfo.currentPage <= 1)) {
            return;
        }

        props.SetPortalWorking('WorkspaceList:pageLeft');
        LoadWorkspaces(props.workspacePageInfo.currentPage - 1, searchWord)
            .finally(() => {
                props.SetPortalIdle('WorkspaceList:pageLeft');
            });
    }

    const goFirstPage = (): void => {
        if ((!props.workspacePageInfo) || (props.workspacePageInfo.currentPage <= 1)) {
            return;
        }

        props.SetPortalWorking('WorkspaceList:goFirstPage');
        LoadWorkspaces(1, searchWord)
            .finally(() => {
                props.SetPortalIdle('WorkspaceList:goFirstPage');
            });
    }

    const totalPageCount = (): number => {
        if (!props.workspacePageInfo) {
            return 0;
        }

        let numPages = Math.floor((props.workspacePageInfo.totalWorkspaces / props.workspacePageInfo.workspacesPerPage));

        if (props.workspacePageInfo.totalWorkspaces % props.workspacePageInfo.workspacesPerPage) {
            numPages++;
        }

        return numPages;
    }

    const isOnLastPage = (): boolean => {
        if (!props.workspacePageInfo) {
            return true;
        }
        if ((props.workspacePageInfo.totalWorkspaces === 0) || (props.workspacePageInfo.workspacesPerPage === 0)) {
            return true;
        }

        const numPages = totalPageCount();

        return (props.workspacePageInfo.currentPage === numPages);
    }

    const pageRight = (): void => {
        if (isOnLastPage()) {
            return;
        }

        // pageInfo can't be undefined if isOnLastPage doesn't return true.
        props.SetPortalWorking('WorkspaceList:pageRight');
        LoadWorkspaces(props.workspacePageInfo!.currentPage + 1, searchWord)
            .finally(() => {
                props.SetPortalIdle('WorkspaceList:pageRight');
            });
    }

    const goLastPage = (): void => {
        if (!props.workspacePageInfo || isOnLastPage()) {
            return;
        }

        const numPages = totalPageCount();

        props.SetPortalWorking('WorkspaceList:goLastPage');
        LoadWorkspaces(numPages, searchWord)
            .finally(() => {
                props.SetPortalIdle('WorkspaceList:goLastPage');
            });
    }

    const upsertWorkspaceEntity = async (
        userIds: string[],
        onComplete: (failed?: 'workspaceNotNamed' | 'unsecureWorkspaceName' | 'unsecureEngagementCode') => void,
        workspace?: EntityDetailResponse
    ): Promise<void> => {
        let workspaceId: string = '';

        props.SetPortalWorking('WorkspaceList:upsertWorkspaceEntity');

        if (!!workspace) {
            workspaceId = workspace.id;
        } else {
            const { displayName, engagementCode } = addWorkspace.addModifyWorkspaceData;

            if (!displayName) {
                onComplete('workspaceNotNamed');
                props.SetPortalIdle('WorkspaceList:upsertWorkspaceEntity');
                return;
            }

            if (InputStringContainsDangerousContent(displayName)) {
                onComplete('unsecureWorkspaceName');
                props.SetPortalIdle('WorkspaceList:upsertWorkspaceEntity');
                return;
            }

            if (engagementCode && InputStringContainsDangerousContent(engagementCode)) {
                onComplete('unsecureEngagementCode');
                props.SetPortalIdle('WorkspaceList:upsertWorkspaceEntity');
                return;
            }

            workspaceId = await UpsertWorkspaceEntity(displayName, engagementCode) || '';
        }

        try {
            if (!!workspaceId) {
                try {
                    await UpdateWorkspaceUsers(workspaceId, userIds);

                    props.onWorkspaceUsersChanged && props.onWorkspaceUsersChanged();
                } catch (exception) {
                    LogException(
                        `Failed adding users to newly created workspace`,
                        new Error(`Failed adding users to newly created workspace`),
                        {
                            workspaceId,
                            users: userIds
                        }
                    );
                }

                MetricsService.tryTrackEvent(EventCategory.Workspace, 'AddComplete', { workspaceId });

                onComplete();

                // If a workspace was added (workspace parameter is undefined) then load the workspaces to refresh the list
                if (!workspace) {
                    LoadWorkspaces(1);
                } else {
                    // Override the state of the user to be able to view the workspace in the redux store
                    // Also, check to see if the logged in user still has access to this workspace by 
                    // looking to see if this user is still in the list of workspace users.   If
                    // not then they can no longer be here, navigate home.
                    if (!!props.loggedInUser) {
                        const loggedInUserHasAccess = !!userIds.find(userId => userId === props.loggedInUser!.id);
                        props.OverrideUserWorkspaceAccess(workspaceId, props.loggedInUser.id, loggedInUserHasAccess);
                        props.UpdateLoggedInUserAccess(workspaceId, loggedInUserHasAccess);
                    }
                }
            }
        }
        finally {
            props.SetPortalIdle('WorkspaceList:upsertWorkspaceEntity');
        }
    }

    const renderTopContent = (): React.ReactNode => {
        if ((errorState) || (!props.textIsAllLoaded)) {
            return (<></>);
        }

        return (
            <>
                <div className={'spaced-row'}>
                    <h1>{props.workspaceListText?.pageTitle || ''}</h1>
                    {!!props.workspaceListText && !!props.commonText && (
                        <div className={'spaced-row'} style={{ marginBottom: '20px' }}>
                            <RefreshButton
                                onClick={() => { loadWorkspacesForCurrentSettings(true); }}
                                disabled={props.portalBusy}
                                style={{ marginRight: InlineStylingValues.ButtonSeparator }}
                            />
                            {props.userCanAddWorkspace && (
                                <Button
                                    type="button"
                                    className={'small'}
                                    onClick={() => {
                                        MetricsService.tryTrackEvent(EventCategory.Workspace, 'AddBegin');
                                        setAddWorkspace({
                                            addWorkspaceModalOpen: true,
                                            addModifyWorkspaceData: {},
                                            showNameRequired: false,
                                            showUnsecureWorkspaceName: false,
                                            showUnsecureEngagementCode: false,
                                        });
                                    }}
                                    disabled={props.portalBusy || failedToLoadUsers || props.disableNewWorkspaces}
                                    color="secondary"
                                >
                                    <FontAwesomeIcon style={{ marginRight: InlineStylingValues.IconSeparator }} icon={faAdd} />{props.workspaceListText?.addWorkspace || ''}
                                </Button>
                            )}
                        </div>
                    )}
                </div>
                <div className={'spaced-row'} style={{ marginBottom: '10px' }}>
                    {!(props.workspacesAreLoading || props.portalBusy) && (
                        <input
                            type={'text'}
                            onChange={
                                (event) => { event.persist(); searchOnChange(event) }}
                            placeholder={props.commonText?.searchByNamePrompt || ''}
                            inputMode={'search'}
                            style={{ width: '250px' }}
                            value={searchWord}
                            id={searchFieldId}
                        >
                        </input>
                    )}
                    {(!!props.workspaces && props.workspaces.length > 0 && !props.workspacesAreLoading) && (
                        <PaginationControls
                            elementsOnPage={props.workspaces.length}
                            elementsPerPage={!!props.workspacePageInfo ? props.workspacePageInfo.workspacesPerPage : 0}
                            totalElements={!!props.workspacePageInfo ? props.workspacePageInfo.totalWorkspaces : 0}
                            pageNumber={!!props.workspacePageInfo ? props.workspacePageInfo.currentPage : 0}
                            pageLeft={pageLeft}
                            pageRight={pageRight}
                            goFirstPage={goFirstPage}
                            goLastPage={goLastPage}
                            isOnLastPage={isOnLastPage}
                            horizontalAligment={'right'}
                            paginationText={props.paginationControlsText}
                            defaultContainerStyle={{ marginTop: '9px' }}
                        />
                    )}
                </div>
            </>
        );
    }

    const importNewDataCollectionButton = (workspace: WorkspaceWithMeta, hardDisable: boolean): React.ReactElement => {
        return (
            <Tooltip title={props.commonText?.newDataCollection || ''}>
                <span>
                    <Button
                        type="button"
                        className={'text small'}
                        style={{
                            marginRight: '10px',
                        }}
                        onClick={() => {
                            const workspaceName = workspace.displayName || workspace.id;

                            props.SetImportCustomerFinancialsActive(
                                true,
                                { workspaceId: workspace.id, workspaceName, }
                            );
                        }}
                        disabled={hardDisable || props.portalBusy}
                        color="primary"
                    >
                        {props.workspaceListText?.newDataCollection || ''}
                    </Button>
                </span>
            </Tooltip>
        );
    }

    const deleteWorkspaceButton = (workspace: WorkspaceWithMeta, hardDisable: boolean): React.ReactElement => {
        const stdThemeColors = GetStdThemeColors(props.brandConfig);

        return (
            <Tooltip title={props.commonText?.deleteWorkspace || ''}>
                <span>
                    <Button
                        type="button"
                        style={{
                            backgroundColor: stdThemeColors.secondaryColor,
                            marginRight: '10px',
                        }}
                        onClick={() => {
                            setConfirmingWorkspace({
                                confirmingWorkspaceId: workspace.id,
                                confirmingWorkspaceName: workspace.displayName,
                            });
                        }}
                        disabled={hardDisable || props.portalBusy}
                        color="secondary"
                    >
                        <FontAwesomeIcon icon={faTrash} />
                    </Button>
                </span>
            </Tooltip>
        )
    }

    const getAccessibleWorkspaceActions = (workspace: WorkspaceWithMeta): JSX.Element[] => {
        let actionContent: JSX.Element[] = [];
        const canManageWorkspaceUsers = WorkspaceActionEnabled(workspace, 'CanManageWorkspaceUsers');

        actionContent.push((
            <div
                key={'shareable-link-icon-fksfjkdfj'}
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                }}
            >
                <NewDataCollectionButton
                    workspaceId={workspace.id}
                    workspaceDisplayName={workspace.displayName || workspace.id}
                    buttonStyle={{
                        marginRight: '10px',
                    }}
                />
                <Tooltip title={props.commonText?.manageWorkspaceUsersTooltip || ''}>
                    <span>
                        <Button
                            type="button"
                            style={{
                                marginRight: '10px',
                            }}
                            onClick={() => {
                                loadWsDetailsAndModifyDetails(workspace.id);
                            }}
                            disabled={props.portalBusy || !canManageWorkspaceUsers}
                            color="secondary"
                        >
                            <FontAwesomeIcon icon={faUserGroup} />
                        </Button>
                    </span>
                </Tooltip>
                {deleteWorkspaceButton(workspace, false)}
            </div>
        ));

        return actionContent;
    }

    const getInaccessibleWorkspaceActions = (workspace: WorkspaceWithMeta): JSX.Element[] => {
        let actionContent: JSX.Element[] = [];

        actionContent.push((
            <div
                key={'shareable-link-icon-fksfjkdfj'}
                style={{
                    display: 'flex',
                    flexDirection: 'row',
                }}
            >
                {importNewDataCollectionButton(workspace, true)}
                {WorkspaceActionEnabled(workspace, 'CanManageWorkspaceUsers') && (
                    <Tooltip title={props.commonText?.manageWorkspaceUsersTooltip || ''}>
                        <span>
                            <Button
                                type="button"
                                style={{
                                    marginRight: '10px',
                                }}
                                onClick={() => {
                                    requestWsAccess(workspace);
                                }}
                                disabled={props.portalBusy}
                                color="secondary"
                            >
                                <FontAwesomeIcon icon={faUserGroup} />
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {!WorkspaceActionEnabled(workspace, 'CanManageWorkspaceUsers') && (
                    <Tooltip title={props.workspaceListText?.requestWorkspaceAccessTooltip || ''}>
                        <span>
                            <Button
                                type="button"
                                style={{
                                    marginRight: '10px',
                                }}
                                onClick={() => {
                                    setQueryConfirmAddToWorkspace(workspace)
                                }}
                                disabled={props.portalBusy}
                                color="primary"
                            >
                                <FontAwesomeIcon icon={faLock} />
                            </Button>
                        </span>
                    </Tooltip>
                )}
                {deleteWorkspaceButton(workspace, true)}
            </div>
        ));

        return actionContent;
    }

    const loadWsDetailsAndModifyDetails = async (workspaceId: string): Promise<void> => {
        props.SetPortalWorking('WorkspaceList:loadWsDetailsAndModifyDetails');
        try {
            const workspace = await LoadWorkspaceDetails(workspaceId, props.allUsers);
            if (!!workspace && !!workspace.entityDetails) {
                props.SetModifyWorkspaceUsersState({
                    workspace: workspace.entityDetails,
                    users: workspace.userInformation.users || []
                });
            } else {
                setErrorState({
                    summaryMessage: props.workspaceListText?.errorFailedRetrievingDetailsGeneral || '',
                });
                LogMessage(
                    `Error retrieving workspace details to allow user to add themselves to a workspace ${workspaceId}`,
                    SeverityLevel.Error,
                    {
                        workspaceId: workspaceId
                    }
                );
            }
        }
        catch (exception) {
            setErrorState({
                summaryMessage: props.workspaceListText?.errorCantGetWorkspaceUsers || '',
                extraInformation: exception.toString(),
            });
            LogException(
                `Error retrieving list of users for a user to add themselves to a workspace ${workspaceId}`,
                exception,
                {
                    workspaceId: workspaceId
                }
            );
        }
        finally {
            props.SetPortalIdle('WorkspaceList:loadWsDetailsAndModifyDetails');
        }
    }

    const requestWsAccess = (workspace: WorkspaceWithMeta): void => {
        const userCanRequestAccess = WorkspaceActionEnabled(workspace, 'CanManageWorkspaceUsers');

        if (userCanRequestAccess) {
            loadWsDetailsAndModifyDetails(workspace.id);
        } else {
            RequestWorkspaceAccess(workspace.id)
                .then((requestResult) => {
                    setAccessRequested({
                        workspace,
                        response: requestResult
                    });
                })
                .catch((exception) => {
                    setErrorState({
                        summaryMessage: props.workspaceListText?.errorCantRequestWorkspaceAccess || '',
                        extraInformation: exception.toString(),
                    });
                })
                .finally(() => {
                    props.SetPortalIdle('WorkspaceList:requestWsAccess');
                });

            props.SetPortalWorking('WorkspaceList:requestWsAccess');
        }
    }

    const getEmailSentMsg = (workspace: WorkspaceWithMeta, response: EntityAccessResponse): React.ReactElement => {
        let allRecipients: string[] = [];

        if (response.notificationsSentTo) {
            response.notificationsSentTo.forEach(sentTo => {
                const user = props.allUsers.find(user => user.id === sentTo);
                if (!!user) {
                    allRecipients.push(FullUserName(user));
                }
            })
        }
        if (response.adminRecipients) {
            response.adminRecipients.forEach(sentTo => {
                allRecipients.push(sentTo);
            })
        }

        return (
            <div>
                {((allRecipients.length > 0) && (
                    <div>
                        <p>{StringFormat(props.workspaceListText?.templateAccessNotificationSent || '{0}', workspace.displayName)}</p>
                        <br />
                        <ul>
                            {(allRecipients.map((sentTo, index) => {
                                return (<li key={`user-name-listed-${index}`}>{sentTo}</li>);
                            }))}
                        </ul>
                    </div>
                ))}
                {((allRecipients.length <= 0) && (<p>{StringFormat(props.workspaceListText?.templateUnableToSendAccessRequest || '{0}', workspace.displayName)}</p>))}
            </div>
        );
    }

    const actionColumnWidth = '150px';
    const containingWindowId = 'strongbox-workspace-list-window';

    if (!props.textIsAllLoaded) {
        return (<></>);
    }

    return (
        <div
            id={containingWindowId}
        >
            <div>
                {(!!props.jobs) && (!!props.jobs.length) && (
                    <div className={'data-container  data-container-secondary'}>
                        <h1>{props.commonText?.pendingDataCollections || ''}</h1>
                        <RunningImportsList
                            key={props.importsListKey}
                            onJobDismiss={(financialRecordId: string, workspaceId: string, fromNav: boolean) => {
                                // If fromNav is true, then a navigation is going to occur and this call won't complete
                                // (because of the navigation) failing randomly somewhere in the middle.  That causes
                                // random behavior.
                                // 
                                // There's no real reason to LoadWorkspaces as the destination of the nav will most likely
                                // do that.

                                if (!fromNav) {
                                    props.SetPortalWorking('WorkspaceList:loadWorkspacesOnJobDismiss');
                                    LoadWorkspaces(1)
                                        .finally(() => {
                                            props.SetPortalIdle('WorkspaceList:loadWorkspacesOnJobDismiss');
                                        });
                                }
                            }}
                            onManageWorkspaceUsers={(workspaceId, submissionId) => {
                                loadWsDetailsAndModifyDetails(workspaceId)
                            }}
                            showExplanatoryRow={true}
                        />
                    </div>
                )}
                <div className={'data-container  data-container-primary'}>
                    {renderTopContent()}
                    <ErrorBanner
                        errorState={errorState}
                        onDefaultActionButton={() => {
                            setErrorState(undefined);
                        }}
                        commonText={props.commonText}
                    />
                    {!errorState && (!props.workspaces || props.workspaces.length === 0) && !props.workspacesAreLoading && (
                        (<h3 style={{ marginTop: '35px' }}>{props.workspaceListText?.noWorkspacesFound || ''}</h3>)
                    )}
                    {!errorState && props.workspaces && props.workspaces.length > 0 && !props.workspacesAreLoading && (<div className={`workspace-list`}>
                        <table>
                            <thead>
                                <tr className={'column-headers'} key={'0000'}>
                                    <th className={'name'}>{props.commonText?.name || ''}</th>
                                    <th className={'newest-submission-actions'}></th>
                                    <th className={'date'}>{props.workspaceListText?.lastActivity || ''}</th>
                                    <th className={'date'}>{props.workspaceListText?.lastDataCollection || ''}</th>
                                    <th style={{ width: actionColumnWidth }}>{props.commonText?.actions || ''}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {
                                    props.workspaces.map(workspace => {
                                        let actionContent: JSX.Element[] = [];

                                        const userHasAccess = WorkspaceActionEnabled(workspace, 'CanViewWorkspace');

                                        if (userHasAccess) {
                                            actionContent = getAccessibleWorkspaceActions(workspace);
                                        } else {
                                            actionContent = getInaccessibleWorkspaceActions(workspace);
                                        }

                                        /* Returns a workspace row */

                                        return (
                                            <tr className={`normal-rows normal-rows-action-container${workspaceActionMsgLocked === workspace.id ? '-lock-visible' : ''}`} key={workspace.id}>
                                                {userHasAccess && (
                                                    <>
                                                        <td className={'name'}>
                                                            <Tooltip title={workspace.displayName}>
                                                                <>
                                                                    <span
                                                                        className={props.portalBusy ? 'disabled' : 'link'}
                                                                        onClick={() => { navigate(`${pathConstants.workspaceDetailsSubmissionList}/${BuildQueryString({ workspaceId: workspace.id, workspaceName: workspace.displayName || '' })}`) }}
                                                                    >
                                                                        {workspace.displayName}
                                                                    </span>
                                                                    <span className='disabled' style={{ display: "block" }}>
                                                                        {workspace.engagementCode}
                                                                    </span>
                                                                </>
                                                            </Tooltip>
                                                        </td>
                                                        <td className={`newest-submission-actions`}>
                                                            {((!workspaceActionMsgLocked) || (workspaceActionMsgLocked === workspace.id)) && (
                                                                <NewestSubmissionActions
                                                                    workspaceId={workspace.id}
                                                                    onError={(msg) => {
                                                                        setErrorState({
                                                                            summaryMessage: msg
                                                                        })
                                                                    }}
                                                                    onLockVisibility={(lock) => {
                                                                        setWorkspaceActionMsgLocked(lock ? workspace.id : undefined);
                                                                    }}
                                                                />
                                                            )}
                                                        </td>
                                                        <td
                                                            className={'date'}
                                                        >
                                                            <Tooltip title={workspace.displayDateFull}>
                                                                <span>{workspace.displayDate}</span>
                                                            </Tooltip>
                                                        </td>
                                                        <td
                                                            className={'date'}
                                                        >
                                                            <Tooltip title={workspace.lastSyncDisplayDateFull}>
                                                                <span>{workspace.lastSyncDisplayDate}</span>
                                                            </Tooltip>
                                                        </td>
                                                    </>
                                                )}
                                                {!userHasAccess && (
                                                    <>
                                                        <td className={'name disabled'}>
                                                            <span
                                                                className={'link disabled-link'}
                                                                title={workspace.displayName}
                                                            >
                                                                {workspace.displayName}
                                                            </span>
                                                            <span style={{ color: "gray", display: "block" }}>
                                                                {workspace.engagementCode}
                                                            </span>
                                                        </td>
                                                        <td></td>
                                                        <td>---</td>
                                                        <td>---</td>
                                                    </>
                                                )}
                                                <td
                                                    className={'actions'}
                                                    style={{ width: actionColumnWidth }}
                                                >
                                                    {actionContent}
                                                </td>
                                            </tr>
                                        );
                                    })
                                }
                            </tbody>
                        </table>
                    </div>)}
                    {!errorState && (!!props.workspaces && props.workspaces.length > 0) && !props.workspacesAreLoading && (
                        <PaginationControls
                            elementsOnPage={props.workspaces.length}
                            elementsPerPage={!!props.workspacePageInfo ? props.workspacePageInfo.workspacesPerPage : 0}
                            totalElements={!!props.workspacePageInfo ? props.workspacePageInfo.totalWorkspaces : 0}
                            pageNumber={!!props.workspacePageInfo ? props.workspacePageInfo.currentPage : 0}
                            pageLeft={pageLeft}
                            pageRight={pageRight}
                            goFirstPage={goFirstPage}
                            goLastPage={goLastPage}
                            isOnLastPage={isOnLastPage}
                            horizontalAligment={'center'}
                            defaultContainerStyle={{ marginTop: '15px' }}
                            paginationText={props.paginationControlsText}
                        />
                    )}
                </div>
            </div>
            {
                !!props.modifyWorkspaceUsers && (
                    <AddModifyWorkspace
                        loggedOnUser={props.loggedInUser}
                        workspace={props.modifyWorkspaceUsers.workspace}
                        currentUsers={props.modifyWorkspaceUsers.users}
                        open={true}
                        allUsers={props.allUsers}
                        onCancel={() => {
                            props.SetModifyWorkspaceUsersState(undefined);
                        }}
                        onSubmit={async (users) => {
                            await upsertWorkspaceEntity(
                                users,
                                (failed) => {
                                    props.SetModifyWorkspaceUsersState(undefined);
                                },
                                props.modifyWorkspaceUsers!.workspace
                            )
                        }}
                        commonText={props.commonText}
                        workspaceManagementText={props.workspaceManagementText}
                    />
                )}
            {
                addWorkspace.addWorkspaceModalOpen && (
                    <AddModifyWorkspace
                        loggedOnUser={props.loggedInUser}
                        commonText={props.commonText}
                        workspaceData={addWorkspace.addModifyWorkspaceData}
                        open={true}
                        errorState={addModifyErrorState}
                        allUsers={props.allUsers}
                        workspaceManagementText={props.workspaceManagementText}
                        onCancel={() => {
                            MetricsService.tryTrackEvent(EventCategory.Workspace, 'AddCanceled');
                            setAddWorkspace({
                                ...addWorkspace,
                                addWorkspaceModalOpen: false,
                            });
                        }}
                        onSubmit={async (users) => {
                            await upsertWorkspaceEntity(
                                users,
                                (failed) => {
                                    if (failed === 'workspaceNotNamed') {
                                        setAddWorkspace({
                                            ...addWorkspace,
                                            showNameRequired: true,
                                        });
                                    } else if (failed === 'unsecureWorkspaceName') {
                                        setAddWorkspace({
                                            ...addWorkspace,
                                            showUnsecureWorkspaceName: true,
                                        });
                                    } else if (failed === 'unsecureEngagementCode') {
                                        setAddWorkspace({
                                            ...addWorkspace,
                                            showUnsecureEngagementCode: true,
                                        });
                                    }
                                    else {
                                        setAddWorkspace({
                                            addWorkspaceModalOpen: false,
                                            addModifyWorkspaceData: {},
                                            showNameRequired: false,
                                            showUnsecureWorkspaceName: false,
                                            showUnsecureEngagementCode: false,
                                        });
                                    }
                                }
                            )
                        }}
                        onWorkspaceDataChange={(workspaceData: AddModifyWorkspaceData) => {
                            let { displayName, engagementCode } = workspaceData;
                            let nameToSet;
                            let showNameRequired = addWorkspace.showNameRequired;

                            if (displayName?.trim() !== '') {
                                nameToSet = displayName;
                                showNameRequired = false;
                            }

                            setAddWorkspace({
                                ...addWorkspace,
                                addModifyWorkspaceData: { ...addWorkspace.addModifyWorkspaceData, displayName: nameToSet, engagementCode: engagementCode },
                                showNameRequired,
                            });
                        }}
                    />
                )}
            {
                confirmingWorkspace &&
                <ConfirmModal
                    onTerminalButton={(yes?: boolean): void => {
                        if (!!yes) {
                            const workspaceId: string = confirmingWorkspace.confirmingWorkspaceId;
                            props.SetPortalWorking('WorkspaceList:ConfirmModal:OnTerminalButton');
                            WorkspaceDelete(workspaceId)
                                .finally(() => {
                                    props.SetPortalIdle('WorkspaceList:ConfirmModal:OnTerminalButton');
                                });
                        }
                        setConfirmingWorkspace(undefined);
                    }}
                    msg={StringFormat(props.workspaceListText?.templateConfirmDeleteWorkspace || '{0}', confirmingWorkspace.confirmingWorkspaceName)}
                    title={StringFormat(props.commonText?.templateDeleteWorkspace || '{0}', confirmingWorkspace.confirmingWorkspaceName)}
                    commonText={props.commonText}
                />
            }
            {
                !!accessRequested && (
                    <ConfirmModal
                        onTerminalButton={(yes?: boolean) => {
                            setAccessRequested(undefined);
                        }}
                        msg={getEmailSentMsg(accessRequested.workspace, accessRequested.response)}
                        title={props.workspaceListText?.accessRequestedConfirmation || ''}
                        modalType={ConfirmModalType.ok}
                        commonText={props.commonText}
                    />
                )
            }
            {
                !!queryConfirmAddToWorkspace &&
                <ConfirmModal
                    onTerminalButton={(yes?: boolean): void => {
                        if (!!yes) {
                            requestWsAccess(queryConfirmAddToWorkspace);
                        }
                        setQueryConfirmAddToWorkspace(undefined);
                    }}
                    msg={
                        StringFormat(props.workspaceListText?.templateConfirmToRequestAccessForWS || '{0}', queryConfirmAddToWorkspace.displayName)
                    }
                    title={
                        StringFormat(props.workspaceListText?.templateRequestAccessTo || '{0}', queryConfirmAddToWorkspace.displayName)
                    }
                    commonText={props.commonText}
                />
            }
        </div>
    );
}

export const WorkspaceList = connect<InjectedReduxState, InjectedActionCreators, WorkspaceListProps, ApplicationState>(
    (appState: ApplicationState) => {
        const basicSettings = GetBasicSettings(appState);
        const runningJobsVersion = GetRunningJobsVersion(appState);
        const workspaceInfo = GetWorkspaceInfo(appState);
        const brandConfig = GetBrandConfig(appState);

        const result: InjectedReduxState = {
            allUsers: GetUsers(appState),
            customerFinancialsTarget: GetImportCustomerFinancialsTarget(appState),
            jobs: GetRunningJobs(appState),
            strongboxUrl: GetStrongboxUrl(appState) || '',
            importsListKey: runningJobsVersion.toString(),
            workspaceListKey: (runningJobsVersion + 1).toString(),
            linkParamsActive: GetShareableLinkParamsFlowSwitch(appState),
            userCanAddWorkspace: LoggedInUserHasAccess(appState, 'CanCreateWorkspace'),
            basicTenantSettings: basicSettings,
            workspacesAreLoading: LoadingWorkspaces(appState),
            workspaces: (!!workspaceInfo) ? workspaceInfo.workspaces : [],
            workspacePageInfo: (!!workspaceInfo) ? workspaceInfo.pageInfo : undefined,
            loggedInUser: GetLoggedInUser(appState),
            portalBusy: GetPortalBusyState(appState),
            brandConfig,
            disableNewWorkspaces: GetNewWorkspacesDisabled(appState),
            modifyWorkspaceUsers: GetModifyWorkspaceUsersState(appState),
            workspaceListText: GetPortalText<PortalTextWorkspaceListPage>(appState, 'workspaceListPage'),
            paginationControlsText: GetPortalText<PortalTextPaginationControls>(appState, 'paginationControls'),
            workspaceManagementText: GetPortalText<PortalTextWorkspaceManagement>(appState, 'workspaceManagement'),
            commonText: GetPortalText<PortalTextCommon>(appState, 'commonText'),
            textIsAllLoaded: false,
            thereAreWorkspaces: false,
        };

        result.textIsAllLoaded =
            !!result.workspaceListText &&
            !!result.paginationControlsText &&
            !!result.workspaceManagementText &&
            !!result.commonText;

        result.thereAreWorkspaces = result.workspaces.length > 0;

        return result;
    },
    dispatch => bindActionCreators(
        {
            ...ImportFinancialsActions,
            ...NewDataCollectionParametersActions,
            ...RunningImportsActions,
            ...WorkspacesActions,
            ...SubmissionHistoryActions,
            ...UIStateActions,
        },
        dispatch
    )
)(WorkspaceListComponent);

